// src/pages/AOPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./AOPage.css";

const API = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
const AO_LOCAL_STORAGE_KEY = "ao_latest_evaluation";
const AO_LOCAL_STORAGE_HISTORY_KEY = "ao_evaluation_history";

/**
 * AO Page (Assessment Orchard) - mengikuti Excel Anda
 * - Pertanyaan 1..25
 * - Conditional Q8, Q10..Q16 berdasarkan Q7
 * - Q17 auto (Multiclone) dari persentase (BB01 + S1) / total pohon
 * - Rekomendasi berurutan sesuai excel, dedup + reason
 */

const CLONE_KEYS = {
  MCC02: "MCC02",
  S2: "S2",
  BB01: "BB01",
  S1: "S1",
  ISC1_SCC11: "ISC1 dan/atau SCC11",
  LOKAL_HYBRID: "Lokal atau hybrid",
  LAINNYA: "Clone Lainnya",
};

function toInt(v) {
  const n = parseInt(String(v ?? "").replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}
function toFloat(v) {
  const s = String(v ?? "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

function isFilled(v) {
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function uniqPush(map, title, payload) {
  if (!map.has(title)) map.set(title, { title, desc: payload.desc || "", reasons: [] });
  if (payload.reason) {
    const rec = map.get(title);
    if (!rec.reasons.includes(payload.reason)) rec.reasons.push(payload.reason);
  }
}

function OptionChips({ name, options, value, onChange }) {
  return (
    <div className="ao-chips">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <label key={opt.value} className={`ao-chip ${active ? "is-active" : ""}`}>
            <input
              type="radio"
              name={name}
              checked={active}
              onChange={() => onChange(opt.value)}
            />
            <span className="ao-chip__label">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

/** ✅ FIX: checkbox jadi grid + 1 baris (checkbox + label)
 *  - className match ke CSS: .ao-checkGrid & .ao-checkItem
 */
function CheckboxGrid({ name = "checkbox-grid", options, values, onChange }) {
  const arr = Array.isArray(values) ? values : [];

  return (
    <div className="ao-checkGrid">
      {options.map((opt) => {
        const checked = arr.includes(opt.value);

        return (
          <label
            key={opt.value}
            className={`ao-checkItem ${checked ? "is-active" : ""}`}
            title={opt.label}
          >
            <input
              type="checkbox"
              name={name}
              checked={checked}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...arr, opt.value]
                  : arr.filter((x) => x !== opt.value);
                onChange(next);
              }}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function FieldBlock({ title, code, required, error, children }) {
  return (
    <div className={`ao-q ${error ? "is-missing" : ""}`}>
      <div className="ao-q__head">
        <div className="ao-q__title">
          {title}
          {required && <span className="ao-required">*</span>}
        </div>
        <div className="ao-q__code">No {code}</div>
      </div>
      <div className="ao-q__body">{children}</div>
      {error && <div className="ao-error">Wajib diisi.</div>}
    </div>
  );
}

export default function AOPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [farmerName, setFarmerName] = useState("");
  const [desa, setDesa] = useState("");
  const [kecamatan, setKecamatan] = useState("");
  const [kabupaten, setKabupaten] = useState("");
  const [provinsi, setProvinsi] = useState("");

  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q4_ph, setQ4_ph] = useState("");
  const [q5, setQ5] = useState("");
  const [q6, setQ6] = useState("");

  const [q7_clones, setQ7_clones] = useState([]);
  const [q8_cloneOther, setQ8_cloneOther] = useState("");

  const [q9_totalTrees, setQ9_totalTrees] = useState("");

  const [q10_mcc02, setQ10_mcc02] = useState("");
  const [q11_s2, setQ11_s2] = useState("");
  const [q12_s1, setQ12_s1] = useState("");
  const [q13_bb01, setQ13_bb01] = useState("");
  const [q14_isc1scc11, setQ14_isc1scc11] = useState("");
  const [q15_other, setQ15_other] = useState("");
  const [q16_lokalhybrid, setQ16_lokalhybrid] = useState("");

  const [q18_age, setQ18_age] = useState("");
  const [q19_suddenDeath, setQ19_suddenDeath] = useState("");

  const [q20_pruning, setQ20_pruning] = useState("");
  const [q21_pest, setQ21_pest] = useState("");
  const [q22_weeds, setQ22_weeds] = useState("");
  const [q23_shade, setQ23_shade] = useState("");

  const [q24_fertilizerFormula, setQ24_fertilizerFormula] = useState([]);
  const [q25_fertilizerDose, setQ25_fertilizerDose] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(AO_LOCAL_STORAGE_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);
      const answers = saved?.answers || {};

      setFarmerName(String(saved?.farmer_name || ""));
      setDesa(String(saved?.desa || ""));
      setKecamatan(String(saved?.kecamatan || ""));
      setKabupaten(String(saved?.kabupaten || ""));
      setProvinsi(String(saved?.provinsi || ""));

      setQ1(String(answers?.q1 || ""));
      setQ2(String(answers?.q2 || ""));
      setQ4_ph(String(answers?.q4_ph || ""));
      setQ5(String(answers?.q5 || ""));
      setQ6(String(answers?.q6 || ""));

      setQ7_clones(Array.isArray(answers?.q7_clones) ? answers.q7_clones : []);
      setQ8_cloneOther(String(answers?.q8_cloneOther || ""));

      setQ9_totalTrees(String(answers?.q9_totalTrees || ""));
      setQ10_mcc02(String(answers?.q10_mcc02 || ""));
      setQ11_s2(String(answers?.q11_s2 || ""));
      setQ12_s1(String(answers?.q12_s1 || ""));
      setQ13_bb01(String(answers?.q13_bb01 || ""));
      setQ14_isc1scc11(String(answers?.q14_isc1scc11 || ""));
      setQ15_other(String(answers?.q15_other || ""));
      setQ16_lokalhybrid(String(answers?.q16_lokalhybrid || ""));

      setQ18_age(String(answers?.q18_age || ""));
      setQ19_suddenDeath(String(answers?.q19_suddenDeath || ""));
      setQ20_pruning(String(answers?.q20_pruning || ""));
      setQ21_pest(String(answers?.q21_pest || ""));
      setQ22_weeds(String(answers?.q22_weeds || ""));
      setQ23_shade(String(answers?.q23_shade || ""));
      setQ24_fertilizerFormula(
        Array.isArray(answers?.q24_fertilizerFormula)
          ? answers.q24_fertilizerFormula
          : String(answers?.q24_fertilizerFormula || "").trim()
            ? [String(answers.q24_fertilizerFormula)]
            : []
      );
      setQ25_fertilizerDose(String(answers?.q25_fertilizerDose || ""));

      setSubmitted(true);
    } catch {
      // abaikan jika localStorage rusak/format lama
    }
  }, []);

  // show conditions
  const hasClone = (cloneValue) => Array.isArray(q7_clones) && q7_clones.includes(cloneValue);

  const showQ8 = hasClone(CLONE_KEYS.LAINNYA);
  const showQ10 = hasClone(CLONE_KEYS.MCC02);
  const showQ11 = hasClone(CLONE_KEYS.S2);
  const showQ12 = hasClone(CLONE_KEYS.S1);
  const showQ13 = hasClone(CLONE_KEYS.BB01);
  const showQ14 = hasClone(CLONE_KEYS.ISC1_SCC11);
  const showQ15 = hasClone(CLONE_KEYS.LAINNYA);
  const showQ16 = hasClone(CLONE_KEYS.LOKAL_HYBRID);
  const hasFertilizerFormula = (formula) =>
    Array.isArray(q24_fertilizerFormula) && q24_fertilizerFormula.includes(formula);

  // Q17 auto multiclone
  const multiclone = useMemo(() => {
    const total = Math.max(0, toInt(q9_totalTrees));
    const bb = Math.max(0, toInt(q13_bb01));
    const s1 = Math.max(0, toInt(q12_s1));
    const bbS1Total = bb + s1;

    if (total <= 0) {
      return { pct: 0, status: "-", note: "Isi total pohon (No 9) agar Multiclone terhitung." };
    }

    const pct = (bbS1Total / total) * 100;
    const selectedClones = Array.isArray(q7_clones) ? q7_clones : [];
    const hasBb = selectedClones.includes(CLONE_KEYS.BB01);
    const hasS1 = selectedClones.includes(CLONE_KEYS.S1);
    const selectedBbOrS1Count = [hasBb, hasS1].filter(Boolean).length;
    const bbPct = (bb / total) * 100;
    const s1Pct = (s1 / total) * 100;

    let status = "B";
    if (selectedClones.length >= 3 && pct >= 30 && pct <= 70) {
      status = "G";
    }
    if (
      selectedClones.length === 2 &&
      selectedBbOrS1Count === 1 &&
      ((hasBb && bbPct >= 30 && bbPct <= 70) || (hasS1 && s1Pct >= 30 && s1Pct <= 70))
    ) {
      status = "M";
    }

    return { pct, status, note: "" };
  }, [q9_totalTrees, q13_bb01, q12_s1, q7_clones]);

  // required validation
  const requiredMissing = useMemo(() => {
    const missing = [];

    if (!isFilled(q1)) missing.push("1");
    if (!isFilled(q2)) missing.push("2/3");
    if (!isFilled(q4_ph)) missing.push("4");
    if (!isFilled(q5)) missing.push("5");
    if (!isFilled(q6)) missing.push("6");
    if (!isFilled(q7_clones)) missing.push("7");
    if (!isFilled(q9_totalTrees)) missing.push("9");
    if (!isFilled(q18_age)) missing.push("18");
    if (!isFilled(q19_suddenDeath)) missing.push("19");
    if (!isFilled(q20_pruning)) missing.push("20");
    if (!isFilled(q21_pest)) missing.push("21");
    if (!isFilled(q22_weeds)) missing.push("22");
    if (!isFilled(q23_shade)) missing.push("23");
    if (!isFilled(q24_fertilizerFormula)) missing.push("24");
    if (!isFilled(q25_fertilizerDose)) missing.push("25");

    if (showQ8 && !isFilled(q8_cloneOther)) missing.push("8");
    if (showQ10 && !isFilled(q10_mcc02)) missing.push("10");
    if (showQ11 && !isFilled(q11_s2)) missing.push("11");
    if (showQ12 && !isFilled(q12_s1)) missing.push("12");
    if (showQ13 && !isFilled(q13_bb01)) missing.push("13");
    if (showQ14 && !isFilled(q14_isc1scc11)) missing.push("14");
    if (showQ15 && !isFilled(q15_other)) missing.push("15");
    if (showQ16 && !isFilled(q16_lokalhybrid)) missing.push("16");

    if (hasClone(CLONE_KEYS.BB01) && !isFilled(q13_bb01)) missing.push("13");
    if (hasClone(CLONE_KEYS.S1) && !isFilled(q12_s1)) missing.push("12");

    return Array.from(new Set(missing));
  }, [
    q1, q2, q4_ph, q5, q6, q7_clones, q8_cloneOther, q9_totalTrees,
    q10_mcc02, q11_s2, q12_s1, q13_bb01, q14_isc1scc11, q15_other, q16_lokalhybrid,
    q18_age, q19_suddenDeath, q20_pruning, q21_pest, q22_weeds, q23_shade,
    q24_fertilizerFormula, q25_fertilizerDose,
    showQ8, showQ10, showQ11, showQ12, showQ13, showQ14, showQ15, showQ16
  ]);

  const profileMissing = useMemo(() => {
    const missing = [];
    if (!isFilled(farmerName)) missing.push("Nama");
    if (!isFilled(desa)) missing.push("Desa");
    if (!isFilled(kecamatan)) missing.push("Kecamatan");
    if (!isFilled(kabupaten)) missing.push("Kabupaten");
    if (!isFilled(provinsi)) missing.push("Provinsi");
    return missing;
  }, [farmerName, desa, kecamatan, kabupaten, provinsi]);

  // ✅ NEW: kondisi "semua G" untuk rekomendasi Pemeliharaan Rutin
  const allGood = useMemo(() => {
    const ph = toFloat(q4_ph);
    const phOK = Number.isFinite(ph) ? ph >= 5.8 : false;

    return (
      q1 === "G" &&
      q2 === "G" &&
      phOK &&
      q5 === "G" &&
      q6 === "G" &&
      multiclone.status === "G" &&
      q19_suddenDeath === "G" &&
      q20_pruning === "G" &&
      q21_pest === "G" &&
      q22_weeds === "G" &&
      q23_shade === "G" &&
      q25_fertilizerDose === "G" &&
      hasFertilizerFormula("NPK Pelangi Kakao + Kapur")
    );
  }, [
    q1, q2, q4_ph, q5, q6,
    multiclone.status,
    q19_suddenDeath, q20_pruning, q21_pest, q22_weeds, q23_shade,
    q24_fertilizerFormula, q25_fertilizerDose
  ]);

  // recommendations
  const recommendations = useMemo(() => {
    const map = new Map();

    if (q1 === "B") {
      uniqPush(map, "Pembuatan Teras atau drainage", {
        reason: "Kondisi tanah: miring/berbatu/tergenang ≤ 2 hari",
      });
    }

    if (q2 === "B") {
      uniqPush(map, "Penambahan pupuk organik atau kompos", {
        reason: "Kesehatan tanah kurang bagus",
      });
    }

    const ph = toFloat(q4_ph);
    if (Number.isFinite(ph) && ph < 5.8) {
      uniqPush(map, "Pengapuran", {
        reason: `pH Rendah < 5.8 (pH: ${ph})`,
      });
    }

    if (q5 === "B") {
      uniqPush(map, "Penanaman Ulang", {
        reason: "Tanaman tidak sehat",
      });
    }

    if (q6 === "M") {
      uniqPush(map, "Sambung Samping", { reason: "Rehabilitasi: campuran namun clone > 50%" });
    }
    if (q6 === "B") {
      uniqPush(map, "Sambung Samping atau Penanaman Ulang", {
        reason: "Rehabilitasi: >70% lokal atau hybrid",
      });
    }

    if (multiclone.status === "M" || multiclone.status === "B") {
      uniqPush(map, "Sambung Samping atau penyulaman clone BB1/S1/ISC1", {
        reason: `Status Multiclone: ${multiclone.status} (${multiclone.pct.toFixed(1)}%)`,
      });
    }

    const age = toInt(q18_age);
    if (age > 30) {
      uniqPush(map, "Penanaman Ulang", { reason: `Usia tanaman > 30 tahun (usia: ${age})` });
    } else if (age > 20) {
      uniqPush(map, "Sambung Samping", { reason: `Usia tanaman > 20 tahun (usia: ${age})` });
    }

    if (q19_suddenDeath === "B") {
      uniqPush(map, "Pengendalian Penyakit Akar", { reason: "Ada tanaman mati tiba-tiba menandakan serangan Jamur Akar" });
    }

    if (q20_pruning === "M" || q20_pruning === "B") {
      uniqPush(map, "Pemangkasan", { reason: "Kualitas pemangkasan" });
    }

    if (q21_pest === "M" || q21_pest === "B") {
      uniqPush(map, "Pengendalian hama penyakit dan sanitasi", { reason: "Serangan sedang/tinggi" });
    }

    if (q22_weeds === "B") {
      uniqPush(map, "Pengendalian rumput", { reason: "Rumput banyak/tinggi/padat" });
    }

    if (q23_shade === "B") {
      uniqPush(map, "Penanaman pohon pelindung yang menghasilkan", { reason: "Pohon pelindung < 16 pohon/ha" });
    }

    if (hasFertilizerFormula("NPK Pelangi kakao")) {
      uniqPush(map, "Penambahan Kapur (Gunakan NPK Pelangi + Kapur)", {
        reason: "Formulasi pupuk: NPK Pelangi kakao",
      });
    }

    const needPelangiKapur = [
      "NPK Phoska + Kapur",
      "NPK Phonska",
      "NPK Phonska + Urea",
      "Urea + TSP + KCL",
      "Urea/ZA",
      "TSP",
      "KCL",
    ];
    const selectedNeedPelangiKapur = needPelangiKapur.filter((formula) =>
      hasFertilizerFormula(formula)
    );
    if (selectedNeedPelangiKapur.length > 0) {
      uniqPush(map, "Gunakan NPK Pelangi + Kapur", {
        reason: `Formulasi pupuk: ${selectedNeedPelangiKapur.join(", ")}`,
      });
    }

    if (hasFertilizerFormula("Pupuk Organik")) {
      uniqPush(map, "Tambahkan Pupuk kimia NPK Pelangi + Kapur", {
        reason: "Formulasi pupuk: hanya pupuk organik",
      });
    }

    if (hasFertilizerFormula("Tidak memupuk 1 tahun terakhir")) {
      uniqPush(map, "Lakukan Pemupukan untuk produksi optimal", {
        reason: "Tidak memupuk 1 tahun terakhir",
      });
    }

    if (q25_fertilizerDose === "M") {
      uniqPush(map, "Tambah dosis pupuk", { reason: "Dosis pupuk 500–900 gr/pohon/tahun" });
    }
    if (q25_fertilizerDose === "B") {
      uniqPush(map, "Tambah dosis pupuk dan aplikasinya", {
        reason: "Dosis < 500 g/pohon/tahun atau aplikasi kurang sesuai",
      });
    }
    if (q25_fertilizerDose === "B_NOFERT") {
      uniqPush(map, "Lakukan Pemupukan untuk produksi optimal", {
        reason: "Tidak memupuk 1 tahun terakhir",
      });
    }

    // ✅ NEW: jika semua kondisi "G" DAN belum ada rekomendasi yang terpicu
    if (map.size === 0 && allGood) {
      uniqPush(map, "Pemeliharaan Rutin", {
        reason: "Semua indikator utama berada pada kategori baik (G).",
        desc: "Pertahankan pemeliharaan: pemangkasan teratur, sanitasi kebun, pemupukan sesuai dosis, dan monitoring hama/penyakit.",
      });
    }

    return Array.from(map.values());
  }, [
    q1, q2, q4_ph, q5, q6,
    multiclone, q18_age, q19_suddenDeath, q20_pruning,
    q21_pest, q22_weeds, q23_shade,
    q24_fertilizerFormula, q25_fertilizerDose,
    allGood
  ]);

  // actions
  const resetAll = () => {
    setSubmitted(false);
    setSaveError("");
    setFarmerName(""); setDesa(""); setKecamatan(""); setKabupaten(""); setProvinsi("");
    setQ1(""); setQ2(""); setQ4_ph(""); setQ5(""); setQ6("");
    setQ7_clones([]); setQ8_cloneOther("");
    setQ9_totalTrees("");
    setQ10_mcc02(""); setQ11_s2(""); setQ12_s1(""); setQ13_bb01(""); setQ14_isc1scc11(""); setQ15_other(""); setQ16_lokalhybrid("");
    setQ18_age(""); setQ19_suddenDeath("");
    setQ20_pruning(""); setQ21_pest(""); setQ22_weeds(""); setQ23_shade("");
    setQ24_fertilizerFormula([]); setQ25_fertilizerDose("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitted(true);
    setSaveError("");
    const el = document.getElementById("ao-result");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });

    if (requiredMissing.length > 0 || profileMissing.length > 0) return;

    const userRaw = localStorage.getItem("user");
    let userId = null;
    try {
      userId = JSON.parse(userRaw || "{}")?.id || null;
    } catch {
      userId = null;
    }

    const payload = {
      user_id: userId,
      farmer_name: farmerName.trim(),
      desa: desa.trim(),
      kecamatan: kecamatan.trim(),
      kabupaten: kabupaten.trim(),
      provinsi: provinsi.trim(),
      answers: {
        q1, q2, q4_ph, q5, q6, q7_clones, q8_cloneOther, q9_totalTrees,
        q10_mcc02, q11_s2, q12_s1, q13_bb01, q14_isc1scc11, q15_other, q16_lokalhybrid,
        q18_age, q19_suddenDeath, q20_pruning, q21_pest, q22_weeds, q23_shade,
        q24_fertilizerFormula, q25_fertilizerDose,
        multiclone,
      },
      recommendations,
      saved_at: new Date().toISOString(),
    };

    try {
      localStorage.setItem(AO_LOCAL_STORAGE_KEY, JSON.stringify(payload));
      const historyRaw = localStorage.getItem(AO_LOCAL_STORAGE_HISTORY_KEY);
      const parsedHistory = JSON.parse(historyRaw || "[]");
      const history = Array.isArray(parsedHistory) ? parsedHistory : [];
      const nextHistory = [payload, ...history].slice(0, 25);
      localStorage.setItem(AO_LOCAL_STORAGE_HISTORY_KEY, JSON.stringify(nextHistory));
    } catch {
      // localStorage dapat diblok browser mode tertentu, biarkan proses backend tetap jalan
    }

    setIsSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (userId) headers["x-user-id"] = String(userId);

      const res = await fetch(`${API}/ao/evaluations`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Gagal menyimpan evaluasi.");
      }
    } catch (err) {
      setSaveError(err.message || "Gagal menyimpan evaluasi.");
    } finally {
      setIsSaving(false);
    }
  };

  // options
  const optQ1 = [
    { label: "Datar / tidak berbatu / tidak tergenang", value: "G" },
    { label: "Miring / berbatu / tergenang air sampai 2 hari", value: "B" },
  ];

  const optQ2 = [
    { label: "Banyak cacing / terjadi pelapukan lapisan daun", value: "G" },
    { label: "Kurang cacing / TIDAK terjadi pelapukan lapisan daun", value: "B" },
  ];

  const optQ5 = [
    { label: "Tumbuh baik jika dipangkas dan dipupuk", value: "G" },
    { label: "Tidak tumbuh baik meskipun dipangkas dan dipupuk", value: "B" },
  ];

  const optQ6 = [
    { label: "Clone (sambung samping / Sambung Pucuk)", value: "G" },
    { label: "Campuran namun tanaman clone >50%", value: "M" },
    { label: ">70% lokal atau hybrid", value: "B" },
  ];

  const optQ7 = [
    { label: "MCC02", value: CLONE_KEYS.MCC02 },
    { label: "S2", value: CLONE_KEYS.S2 },
    { label: "BB01", value: CLONE_KEYS.BB01 },
    { label: "S1", value: CLONE_KEYS.S1 },
    { label: "ISC1 dan/atau SCC11", value: CLONE_KEYS.ISC1_SCC11 },
    { label: "Lokal atau hybrid", value: CLONE_KEYS.LOKAL_HYBRID },
    { label: "Clone Lainnya", value: CLONE_KEYS.LAINNYA },
  ];

  const optQ19 = [
    { label: "Ya", value: "B" },
    { label: "Tidak", value: "G" },
  ];

  const optQ20 = [
    {
      label:
        "Cahaya matahari merata, jarak pandang >50 m, tunas air <10%, batang tunggal, cabang utama 2–3 seimbang, tinggi maks 3,5 m",
      value: "G",
    },
    {
      label:
        "Cahaya matahari merata, jarak pandang <50 m, tunas air <25%, batang tunggal, cabang utama >3 seimbang, tinggi maks 4,5 m",
      value: "M",
    },
    {
      label:
        "Cahaya matahari tidak merata, jarak pandang <10 m, tunas air >25%, batang ganda, cabang utama >3 tidak seimbang, tinggi >4,5 m",
      value: "B",
    },
  ];

  const optQ21 = [
    { label: "Serangan rendah kurang dari 10%", value: "G" },
    { label: "Serangan sedang 10% - 25%", value: "M" },
    { label: "Serangan tinggi lebih dari 25%", value: "B" },
  ];

  const optQ22 = [
    { label: "Sedikit rumput dibawah pohon", value: "G" },
    { label: "Rumput dibawah pohon banyak, tinggi, padat", value: "B" },
  ];

  const optQ23 = [
    { label: "Lebih dari 16 pohon per hektar pohon pelindung (durian, alpukat, sengon, dll)", value: "G" },
    { label: "Kurang dari 16 pohon per hektar pohon pelindung (durian, alpukat, sengon, dll)", value: "B" },
  ];

  const optQ24 = [
    { label: "NPK Pelangi Kakao + Kapur", value: "NPK Pelangi Kakao + Kapur" },
    { label: "NPK Fertila kakao + Nitrabor atau karate plus", value: "NPK Fertila kakao + Nitrabor atau karate plus" },
    { label: "NPK Pelangi kakao", value: "NPK Pelangi kakao" },
    { label: "NPK Phoska + Kapur", value: "NPK Phoska + Kapur" },
    { label: "NPK Phonska", value: "NPK Phonska" },
    { label: "NPK Phonska + Urea", value: "NPK Phonska + Urea" },
    { label: "Urea + TSP + KCL", value: "Urea + TSP + KCL" },
    { label: "Urea/ZA", value: "Urea/ZA" },
    { label: "TSP", value: "TSP" },
    { label: "KCL", value: "KCL" },
    { label: "Pupuk Organik", value: "Pupuk Organik" },
    { label: "Tidak memupuk 1 tahun terakhir", value: "Tidak memupuk 1 tahun terakhir" },
  ];

  const optQ25 = [
    {
      label:
        "Dosis >900 gram/pohon/tahun, dibenam atau tertutup daun, aplikasi minimal 2 kali/tahun",
      value: "G",
    },
    {
      label:
        "Dosis 500–900 gram/pohon/tahun, dibenam atau tertutup daun, aplikasi minimal 2 kali/tahun",
      value: "M",
    },
    {
      label:
        "Dosis <500 gram/pohon/tahun, dihambur & terbuka, aplikasi 1 kali/tahun",
      value: "B",
    },
    {
      label: "Tidak memupuk 1 tahun terakhir",
      value: "B_NOFERT",
    },
  ];

  const handleFertilizerFormulaChange = (nextValues) => {
    const noFertilizer = "Tidak memupuk 1 tahun terakhir";
    const next = Array.isArray(nextValues) ? nextValues : [];

    if (next.includes(noFertilizer) && !q24_fertilizerFormula.includes(noFertilizer)) {
      setQ24_fertilizerFormula([noFertilizer]);
      return;
    }

    if (next.includes(noFertilizer) && next.length > 1) {
      setQ24_fertilizerFormula(next.filter((value) => value !== noFertilizer));
      return;
    }

    setQ24_fertilizerFormula(next);
  };

  return (
    <div className="ao-page">
      <div className="ao-hero">
        <div className="ao-hero__inner">
          <h1>Nilai kebun anda dan dapatkan rekomendasinya untuk mendapatkan produksi maksimal!</h1>

          <div className="ao-heroRow">
            <div className="ao-primaryCard">
              <div className="ao-primaryCard__head">
                <div className="ao-primaryCard__title">Data Petani & Lokasi</div>
              </div>
              <div className="ao-primaryCard__body">
                <div className="ao-profileGrid">
                  <label className={`ao-field ${submitted && !isFilled(farmerName) ? "is-missing" : ""}`}>
                    <span>Nama</span>
                    <input
                      className="ao-input"
                      type="text"
                      placeholder="Masukkan nama"
                      value={farmerName}
                      onChange={(e) => setFarmerName(e.target.value)}
                    />
                  </label>

                  <label className={`ao-field ${submitted && !isFilled(desa) ? "is-missing" : ""}`}>
                    <span>Desa</span>
                    <input
                      className="ao-input"
                      type="text"
                      placeholder="Masukkan desa"
                      value={desa}
                      onChange={(e) => setDesa(e.target.value)}
                    />
                  </label>

                  <label className={`ao-field ${submitted && !isFilled(kecamatan) ? "is-missing" : ""}`}>
                    <span>Kecamatan</span>
                    <input
                      className="ao-input"
                      type="text"
                      placeholder="Masukkan kecamatan"
                      value={kecamatan}
                      onChange={(e) => setKecamatan(e.target.value)}
                    />
                  </label>

                  <label className={`ao-field ${submitted && !isFilled(kabupaten) ? "is-missing" : ""}`}>
                    <span>Kabupaten</span>
                    <input
                      className="ao-input"
                      type="text"
                      placeholder="Masukkan kabupaten"
                      value={kabupaten}
                      onChange={(e) => setKabupaten(e.target.value)}
                    />
                  </label>

                  <label className={`ao-field ${submitted && !isFilled(provinsi) ? "is-missing" : ""}`}>
                    <span>Provinsi</span>
                    <input
                      className="ao-input"
                      type="text"
                      placeholder="Masukkan provinsi"
                      value={provinsi}
                      onChange={(e) => setProvinsi(e.target.value)}
                    />
                  </label>
                </div>
                {submitted && profileMissing.length > 0 && (
                  <div className="ao-error">Data petani & lokasi wajib diisi: {profileMissing.join(", ")}.</div>
                )}
                {saveError ? <div className="ao-error">{saveError}</div> : null}
              </div>
            </div>

            <div className="ao-heroActions">
              <button className="ao-btn ao-btn--ghost" onClick={resetAll} type="button">
                Ulangi Evaluasi
              </button>
              <button
                className="ao-btn"
                onClick={submit}
                type="button"
                disabled={requiredMissing.length > 0 || profileMissing.length > 0 || isSaving}
                title={requiredMissing.length > 0 || profileMissing.length > 0 ? "Lengkapi seluruh data wajib dulu" : ""}
              >
                Lihat Rekomendasi!
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ao-wrap">
        <div className="ao-grid">
          {/* FORM */}
          <div className="ao-main">
            <div className="ao-card">
              <div className="ao-card__head">
                <h2>evaluasi Kondisi Kebun Anda!</h2>
                <div className="ao-card__mini">
                  <span className="dot" /> Pertanyaan bertanda <b>*</b> wajib diisi!
                </div>
              </div>

              <FieldBlock title="Kondisi tanah kebun" code="1" required error={submitted && !isFilled(q1)}>
                <OptionChips name="q1" options={optQ1} value={q1} onChange={setQ1} />
              </FieldBlock>

              <FieldBlock title="Kesehatan Tanah" code="2" required error={submitted && !isFilled(q2)}>
                <OptionChips name="q2" options={optQ2} value={q2} onChange={setQ2} />
              </FieldBlock>

              <FieldBlock title="pH Tanah" code="3" required error={submitted && !isFilled(q4_ph)}>
                <div className="ao-inline">
                  <input
                    className="ao-input"
                    type="number"
                    step="0.1"
                    placeholder="Contoh: 5.6"
                    value={q4_ph}
                    onChange={(e) => setQ4_ph(e.target.value)}
                  />
                </div>
              </FieldBlock>

              <FieldBlock title="Kesehatan tanaman" code="4" required error={submitted && !isFilled(q5)}>
                <OptionChips name="q5" options={optQ5} value={q5} onChange={setQ5} />
              </FieldBlock>

              <FieldBlock title="Metode rehabilitasi" code="5" required error={submitted && !isFilled(q6)}>
                <OptionChips name="q6" options={optQ6} value={q6} onChange={setQ6} />
              </FieldBlock>

              <FieldBlock
                title="Jenis clone yang dikembangkan"
                code="6"
                required
                error={submitted && !isFilled(q7_clones)}
              >
                <CheckboxGrid name="q7_clones" options={optQ7} values={q7_clones} onChange={setQ7_clones} />
              </FieldBlock>

              {showQ8 && (
                <FieldBlock
                  title="Jenis clone lain yang dikembangkan"
                  code="7"
                  required
                  error={submitted && !isFilled(q8_cloneOther)}
                >
                  <input
                    className="ao-input"
                    type="text"
                    placeholder="Tulis nama clone lainnya…"
                    value={q8_cloneOther}
                    onChange={(e) => setQ8_cloneOther(e.target.value)}
                  />
                </FieldBlock>
              )}

              <FieldBlock
                title="Berapa total jumlah pohon pada kebun Anda?"
                code="8"
                required
                error={submitted && !isFilled(q9_totalTrees)}
              >
                <input
                  className="ao-input"
                  type="number"
                  placeholder="Contoh: 500"
                  value={q9_totalTrees}
                  onChange={(e) => setQ9_totalTrees(e.target.value)}
                />
              </FieldBlock>

              {showQ10 && (
                <FieldBlock title="Berapa jumlah pohon clone MCC02?" code="9" required error={submitted && !isFilled(q10_mcc02)}>
                  <input className="ao-input" type="number" value={q10_mcc02} onChange={(e) => setQ10_mcc02(e.target.value)} />
                </FieldBlock>
              )}

              {showQ11 && (
                <FieldBlock title="Berapa jumlah pohon clone S2?" code="10" required error={submitted && !isFilled(q11_s2)}>
                  <input className="ao-input" type="number" value={q11_s2} onChange={(e) => setQ11_s2(e.target.value)} />
                </FieldBlock>
              )}

              {showQ12 && (
                <FieldBlock title="Berapa jumlah pohon clone S1?" code="11" required error={submitted && !isFilled(q12_s1)}>
                  <input className="ao-input" type="number" value={q12_s1} onChange={(e) => setQ12_s1(e.target.value)} />
                </FieldBlock>
              )}

              {showQ13 && (
                <FieldBlock title="Berapa jumlah pohon clone BB01?" code="12" required error={submitted && !isFilled(q13_bb01)}>
                  <input className="ao-input" type="number" value={q13_bb01} onChange={(e) => setQ13_bb01(e.target.value)} />
                </FieldBlock>
              )}

              {showQ14 && (
                <FieldBlock title="Berapa jumlah pohon clone ISC1 atau SCC11?" code="13" required error={submitted && !isFilled(q14_isc1scc11)}>
                  <input className="ao-input" type="number" value={q14_isc1scc11} onChange={(e) => setQ14_isc1scc11(e.target.value)} />
                </FieldBlock>
              )}

              {showQ15 && (
                <FieldBlock title="Berapa jumlah pohon clone Lainnya?" code="14" required error={submitted && !isFilled(q15_other)}>
                  <input className="ao-input" type="number" value={q15_other} onChange={(e) => setQ15_other(e.target.value)} />
                </FieldBlock>
              )}

              {showQ16 && (
                <FieldBlock title="Berapa jumlah pohon lokal atau hybrid?" code="15" required error={submitted && !isFilled(q16_lokalhybrid)}>
                  <input className="ao-input" type="number" value={q16_lokalhybrid} onChange={(e) => setQ16_lokalhybrid(e.target.value)} />
                </FieldBlock>
              )}

              {/* No 17 auto */}
              <div className="ao-q ao-q--auto">
                <div className="ao-q__head">
                  <div className="ao-q__title">Multiclone <span className="ao-muted"></span></div>
                  <div className="ao-q__code">Auto</div>
                </div>
                <div className="ao-q__body">
                  <div className="ao-auto">
                    <div className="ao-auto__big">{multiclone.status === "-" ? "—" : multiclone.status}</div>
                    <div className="ao-auto__meta">
                      <div className="ao-auto__line">
                        Persentase (BB01 + S1) = <b>{multiclone.status === "-" ? "—" : `${multiclone.pct.toFixed(1)}%`}</b>
                      </div>
                    </div>
                  </div>
                  {multiclone.note && <div className="ao-note">{multiclone.note}</div>}
                </div>
              </div>

              <FieldBlock title="Usia tanaman (tahun) contoh: 10" code="16" required error={submitted && !isFilled(q18_age)}>
                <div className="ao-inline">
                  <input
                    className="ao-input"
                    type="number"
                    placeholder="Contoh: 10"
                    value={q18_age}
                    onChange={(e) => setQ18_age(e.target.value)}
                  />
                </div>
              </FieldBlock>

              <FieldBlock title="Apakah saat ini ada tanaman yang mati secara tiba-tiba?" code="17" required error={submitted && !isFilled(q19_suddenDeath)}>
                <OptionChips name="q19" options={optQ19} value={q19_suddenDeath} onChange={setQ19_suddenDeath} />
              </FieldBlock>

              <FieldBlock title="Bagaimana kualitas pemangkasannya" code="18" required error={submitted && !isFilled(q20_pruning)}>
                <div className="ao-stackRadio">
                  {optQ20.map((o) => (
                    <label key={o.value} className={`ao-radioCard ${q20_pruning === o.value ? "is-active" : ""}`}>
                      <input type="radio" name="q20" checked={q20_pruning === o.value} onChange={() => setQ20_pruning(o.value)} />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </FieldBlock>

              <FieldBlock title="Tingkat serangan hama dan penyakit" code="19" required error={submitted && !isFilled(q21_pest)}>
                <OptionChips name="q21" options={optQ21} value={q21_pest} onChange={setQ21_pest} />
              </FieldBlock>

              <FieldBlock title="Kondisi rumput (gulma)" code="20" required error={submitted && !isFilled(q22_weeds)}>
                <OptionChips name="q22" options={optQ22} value={q22_weeds} onChange={setQ22_weeds} />
              </FieldBlock>

              <FieldBlock title="Kondisi pohon pelindung" code="21" required error={submitted && !isFilled(q23_shade)}>
                <div className="ao-stackRadio">
                  {optQ23.map((o) => (
                    <label key={o.value} className={`ao-radioCard ${q23_shade === o.value ? "is-active" : ""}`}>
                      <input type="radio" name="q23" checked={q23_shade === o.value} onChange={() => setQ23_shade(o.value)} />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </FieldBlock>

              <FieldBlock title="Formulasi Pupuk" code="22" required error={submitted && !isFilled(q24_fertilizerFormula)}>
                <CheckboxGrid
                  name="q24_fertilizerFormula"
                  options={optQ24}
                  values={q24_fertilizerFormula}
                  onChange={handleFertilizerFormulaChange}
                />
              </FieldBlock>

              <FieldBlock title="Dosis Pupuk" code="23" required error={submitted && !isFilled(q25_fertilizerDose)}>
                <div className="ao-stackRadio">
                  {optQ25.map((o) => (
                    <label key={o.value} className={`ao-radioCard ${q25_fertilizerDose === o.value ? "is-active" : ""}`}>
                      <input type="radio" name="q25" checked={q25_fertilizerDose === o.value} onChange={() => setQ25_fertilizerDose(o.value)} />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </FieldBlock>

              <div className="ao-bottomBar">
                <button className="ao-btn ao-btn--ghost" onClick={resetAll} type="button">
                  Ulangi Evaluasi
                </button>
                <button className="ao-btn" onClick={submit} type="button" disabled={requiredMissing.length > 0 || profileMissing.length > 0 || isSaving}>
                  {isSaving ? "Menyimpan..." : "dapatkan Rekomendasi"}
                </button>
              </div>
            </div>
          </div>

          {/* RESULT SIDE */}
          <div className="ao-side">
            <div className="ao-sticky">
              <div id="ao-result" className="ao-card">
                <div className="ao-card__head">
                  <h3>Detail Rekomendasi</h3>
                  <div className="ao-pill">{recommendations.length}</div>
                </div>

                {!submitted ? (
                  <div className="ao-muted">
                    Klik <b>Lihat Rekomendasi</b> untuk melihat hasil.
                  </div>
                ) : requiredMissing.length > 0 ? (
                  <div className="ao-empty">
                    Masih ada pertanyaan wajib yang belum lengkap. Lengkapi dulu No:{" "}
                    <b>{requiredMissing.join(", ")}</b>.
                  </div>
                ) : recommendations.length === 0 ? (
                  <div className="ao-empty">Tidak ada rekomendasi yang terpicu dari jawaban Anda.</div>
                ) : (
                  <div className="ao-result">
                    {recommendations.map((r, idx) => (
                      <div key={r.title} className="ao-rec">
                        <div className="ao-rec__top">
                          <div className="ao-rec__title">
                            <span className="ao-rec__num">{idx + 1}.</span> {r.title}
                          </div>
                        </div>

                        {r.desc ? <div className="ao-rec__desc">{r.desc}</div> : null}

                        {r.reasons?.length > 0 && (
                          <div className="ao-reasons">
                            <div className="ao-reasons__line">
                              <span className="ao-reasons__label">Karena:</span>{" "}
                              <span className="ao-reasons__text">{r.reasons.join("; ")}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ao-card ao-card--soft">
                <div className="ao-card__head">
                  <h3>Informasi Penting</h3>
                </div>

                {/* ✅ CTA sesuai permintaan */}
                <div className="ao-ctaNote">
                  <a className="ao-ctaLink" href="/agriservis">
                    KLIK DISINI
                  </a>{" "}
                  untuk konsultasi, order layanan jasa atau produk Sahabat Kakao terdekat
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
